import type { PlayoffFormat, RegistrationIntakeType, ScoringSettings, SeasonRosterIntake, Weekday } from './tournament';

/**
 * League configuration - the singleton that defines the league running on
 * this deployment. Each Cloudflare Pages deploy = one league. It controls
 * League HQ, public league/event pages, registration defaults, and outbound
 * communication defaults. It does not control the Scorekeeper company
 * marketing site.
 */
export interface LeagueConfig {
  /** Display name used in League HQ and league-owned public pages. */
  name: string;
  /** One-line positioning shown on league-owned public surfaces. */
  tagline?: string;
  /** Public contact email used on league-owned pages and as a comms fallback. */
  contactEmail?: string;
  /** Default rink/location used to prefill season and tournament setup. */
  homeRink?: LeagueHomeRink;
  /** Sport the league runs. Roller Hockey is the surfaced v1 default. */
  sport: string;
  /**
   * Legacy/default division labels. Kept for compatibility; new UI edits
   * eventDefaults.divisionLabels and mirrors the value here.
   */
  ageGroups: string[];
  /** Branding tokens applied to league surfaces at runtime. */
  branding: BrandingConfig;
  /** Defaults used only when creating new tournaments/seasons. */
  eventDefaults: LeagueEventDefaults;
  /** League-wide communication governance and reusable templates. */
  communicationSettings: LeagueCommunicationSettings;
  /** Custom profile fields managed by the league owner. Values live on Profile.attributes. */
  directoryFields: LeagueDirectoryField[];
}

export interface BrandingConfig {
  /**
   * Primary brand color as a CSS hex (`#RRGGBB`). Drives `--brand-arc`. The
   * BrandingContext converts to `oklch()` at apply time so it composes with
   * the rest of the token system.
   */
  primaryColor: string;
  /** When false, public event/registration pages keep the Scorekeeper default. */
  applyToPublic: boolean;
  /** R2-backed asset id for the league logo. */
  logoAssetId?: string;
  /** Original logo file name for display in Settings. */
  logoFileName?: string;
}

export interface LeagueHomeRink {
  name: string;
  address: string;
  postalCode?: string;
  city?: string;
  region?: string;
}

export type LeagueDirectoryFieldType = 'text' | 'number' | 'date' | 'select' | 'checkbox';

export interface LeagueDirectoryField {
  id: string;
  key: string;
  label: string;
  type: LeagueDirectoryFieldType;
  required?: boolean;
  showInTable?: boolean;
  registrationField?: boolean;
  options?: string[];
}

export type LeagueModuleKey = 'locker' | 'stands' | 'frontDesk' | 'proShop' | 'concessions' | 'gamification';

export interface LeagueEventDefaults {
  divisionLabels: string[];
  rinkCount: number;
  rinkNamePattern: string;
  tournamentTypeId: string;
  seasonTypeId: string;
  modules: Record<LeagueModuleKey, boolean>;
  season: {
    weekdays: Weekday[];
    firstPuckTime: string;
    gamesPerRinkPerNight: number;
    periodCount: number;
    periodLengthMinutes: number;
    overtimeFormat?: NonNullable<ScoringSettings['overtimeFormat']>;
    overtimeMinutes?: number;
    overtimeSkaters?: number;
    shootoutRoundCount?: number;
    pointsRegulationWin?: number;
    pointsOvertimeWin?: number;
    pointsShootoutWin?: number;
    pointsOvertimeLoss?: number;
    pointsShootoutLoss?: number;
    pointsTie?: number;
    tiebreakers: ScoringSettings['tiebreakers'];
    rosterIntake: SeasonRosterIntake;
    registrationFeeCents: number;
    registrationIntakeType: RegistrationIntakeType;
    registrationRequireWaiver: boolean;
    registrationAutoApprove: boolean;
    playoffsEnabled: boolean;
    playoffFormat: PlayoffFormat;
    playoffQualifyingTeams: number;
  };
}

export interface LeagueCommunicationTemplate {
  id: string;
  label: string;
  subject: string;
  body: string;
}

export interface LeagueCommunicationSettings {
  senderName: string;
  replyToEmail: string;
  replyToLabel: string;
  footerText: string;
  optOutText: string;
  templates: LeagueCommunicationTemplate[];
}

export const DEFAULT_COMMUNICATION_TEMPLATES: LeagueCommunicationTemplate[] = [
  {
    id: 'welcome',
    label: 'Welcome / intro',
    subject: 'Welcome to {{tournament}}',
    body: 'Hey team - looking forward to seeing you at {{tournament}}. Stay tuned for the schedule and any logistics updates.',
  },
  {
    id: 'schedule',
    label: 'Schedule released',
    subject: '{{tournament}} schedule is live',
    body: 'The schedule is published. Check the Stands page or your Scorekeeper inbox for your games.',
  },
  {
    id: 'crew',
    label: 'Scorekeeper / ref assignment',
    subject: 'Your assignment for {{tournament}}',
    body: 'You have been assigned to upcoming games. Open your Scorekeeper inbox for details and confirmation.',
  },
  {
    id: 'delay',
    label: 'Delay / update',
    subject: 'Heads up - schedule update',
    body: 'A short update for {{tournament}}: please check the Front Desk page for the latest schedule and rink information.',
  },
  {
    id: 'captain',
    label: 'Captain reminder',
    subject: 'Captains - confirm your roster',
    body: 'Captains, please confirm your roster and arrival time before puck drop.',
  },
];

export const DEFAULT_EVENT_DEFAULTS: LeagueEventDefaults = {
  divisionLabels: ['Adult'],
  rinkCount: 1,
  rinkNamePattern: 'Rink {{n}}',
  tournamentTypeId: 'ironman',
  seasonTypeId: 'roller-season',
  modules: {
    locker: true,
    stands: true,
    frontDesk: true,
    proShop: false,
    concessions: false,
    gamification: false,
  },
  season: {
    weekdays: [],
    firstPuckTime: '19:00',
    gamesPerRinkPerNight: 3,
    periodCount: 3,
    periodLengthMinutes: 12,
    overtimeFormat: 'straight_shootout',
    overtimeMinutes: 5,
    overtimeSkaters: 3,
    shootoutRoundCount: 3,
    pointsRegulationWin: 2,
    pointsOvertimeWin: 2,
    pointsShootoutWin: 2,
    pointsOvertimeLoss: 1,
    pointsShootoutLoss: 1,
    pointsTie: 1,
    tiebreakers: ['wins', 'goal_diff', 'goals_for', 'head_to_head', 'fewest_pims'],
    rosterIntake: 'later',
    registrationFeeCents: 5000,
    registrationIntakeType: 'individual',
    registrationRequireWaiver: true,
    registrationAutoApprove: false,
    playoffsEnabled: true,
    playoffFormat: 'single_elim',
    playoffQualifyingTeams: 4,
  },
};

export const DEFAULT_COMMUNICATION_SETTINGS: LeagueCommunicationSettings = {
  senderName: 'The Scorekeeper',
  replyToEmail: '',
  replyToLabel: 'League office',
  footerText: 'Sent by {{league}} through The Scorekeeper.',
  optOutText: 'If this message reached you in error, contact the league office to update your communication preferences.',
  templates: DEFAULT_COMMUNICATION_TEMPLATES,
};

export const DEFAULT_DIRECTORY_FIELDS: LeagueDirectoryField[] = [
  { id: 'field-school', key: 'school', label: 'School', type: 'text', showInTable: false, registrationField: true },
  { id: 'field-age-group', key: 'ageGroup', label: 'Age group', type: 'text', showInTable: false, registrationField: true },
  { id: 'field-position', key: 'position', label: 'Position', type: 'text', showInTable: true, registrationField: true },
  { id: 'field-skill-level', key: 'skillLevel', label: 'Skill level', type: 'text', showInTable: false, registrationField: true },
  { id: 'field-guardian-name', key: 'guardianName', label: 'Guardian name', type: 'text', showInTable: false, registrationField: true },
  { id: 'field-emergency-contact', key: 'emergencyContact', label: 'Emergency contact', type: 'text', showInTable: false, registrationField: true },
];

export function normalizeLeagueConfig(input?: Partial<LeagueConfig> | null): LeagueConfig {
  const incoming = input ?? {};
  const eventDefaults: Partial<LeagueEventDefaults> = incoming.eventDefaults ?? {};
  const communicationSettings: Partial<LeagueCommunicationSettings> = incoming.communicationSettings ?? {};
  const homeRink = {
    ...(DEFAULT_LEAGUE.homeRink ?? {}),
    ...(incoming.homeRink ?? {}),
  };
  return {
    ...DEFAULT_LEAGUE,
    ...incoming,
    branding: {
      ...DEFAULT_LEAGUE.branding,
      ...(incoming.branding ?? {}),
    },
    sport: incoming.sport ?? DEFAULT_LEAGUE.sport,
    ageGroups: incoming.ageGroups ?? [...DEFAULT_LEAGUE.ageGroups],
    homeRink: {
      name: homeRink.name ?? '',
      address: homeRink.address ?? '',
      postalCode: homeRink.postalCode ?? '',
      city: homeRink.city ?? '',
      region: homeRink.region ?? '',
    },
    eventDefaults: {
      ...DEFAULT_EVENT_DEFAULTS,
      ...eventDefaults,
      seasonTypeId:
        eventDefaults.seasonTypeId === 'ironman-season'
          ? DEFAULT_EVENT_DEFAULTS.seasonTypeId
          : eventDefaults.seasonTypeId ?? DEFAULT_EVENT_DEFAULTS.seasonTypeId,
      divisionLabels:
        eventDefaults.divisionLabels ??
        incoming.ageGroups ??
        [...DEFAULT_EVENT_DEFAULTS.divisionLabels],
      modules: {
        ...DEFAULT_EVENT_DEFAULTS.modules,
        ...(eventDefaults.modules ?? {}),
      },
      season: {
        ...DEFAULT_EVENT_DEFAULTS.season,
        ...(eventDefaults.season ?? {}),
        tiebreakers: eventDefaults.season?.tiebreakers?.length
          ? eventDefaults.season.tiebreakers
          : DEFAULT_EVENT_DEFAULTS.season.tiebreakers,
      },
    },
    communicationSettings: {
      ...DEFAULT_COMMUNICATION_SETTINGS,
      ...communicationSettings,
      templates: communicationSettings.templates?.length
        ? communicationSettings.templates
        : DEFAULT_COMMUNICATION_SETTINGS.templates,
    },
    directoryFields: normalizeDirectoryFields(incoming.directoryFields),
  };
}

function normalizeDirectoryFields(fields?: LeagueDirectoryField[] | null): LeagueDirectoryField[] {
  const order: string[] = [];
  const byKey = new Map<string, LeagueDirectoryField>();
  for (const field of DEFAULT_DIRECTORY_FIELDS) {
    const key = field.key.trim();
    if (!key) continue;
    order.push(key);
    byKey.set(key, field);
  }
  for (const field of fields ?? []) {
    const key = field.key.trim();
    if (!key) continue;
    if (!byKey.has(key)) order.push(key);
    byKey.set(key, {
      ...(byKey.get(key) ?? {}),
      ...field,
      key,
    });
  }
  return order.flatMap((key) => {
    const field = byKey.get(key);
    if (!field) return [];
    return [{
      ...field,
      id: field.id || `field-${key}`,
      key,
      label: field.label.trim() || key,
      type: field.type ?? 'text',
      required: Boolean(field.required),
      showInTable: Boolean(field.showInTable),
      registrationField: Boolean(field.registrationField),
      options: field.options ?? [],
    }];
  });
}

/** Default league used by store migration and the demo seed. */
export const DEFAULT_LEAGUE: LeagueConfig = {
  name: 'The Scorekeeper',
  tagline: 'One league. Tournaments and seasons, run end to end.',
  contactEmail: '',
  homeRink: {
    name: '',
    address: '',
    postalCode: '',
    city: '',
    region: '',
  },
  sport: 'Roller Hockey',
  ageGroups: ['Adult'],
  branding: {
    primaryColor: '#3b82f6',
    applyToPublic: true,
  },
  eventDefaults: DEFAULT_EVENT_DEFAULTS,
  communicationSettings: DEFAULT_COMMUNICATION_SETTINGS,
  directoryFields: DEFAULT_DIRECTORY_FIELDS,
};
